import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "./utils/index.js";
import { FileText, Home, Upload, BarChart3, Settings, Building2, Users, LogOut, User, ChevronDown, Shield, Menu, X } from "lucide-react";
import { AuthService } from "./Entities/AuthService";
import ProfileModal from "./Components/ProfileModal";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/Components/ui/sidebar";

const getNavigationItems = (user) => {
  const items = [
    {
      title: "Dashboard",
      url: createPageUrl("Dashboard"),
      icon: Home,
    },
    {
      title: "Parcerias",
      url: createPageUrl("Partnerships"),
      icon: FileText,
    },
    {
      title: "Gráficos Analíticos",
      url: createPageUrl("Reports"),
      icon: BarChart3,
    },
    {
      title: "Relatório por OSC",
      url: createPageUrl("OSCReport"),
      icon: Building2,
    },
  ];

  // Adicionar menu de importação apenas para não-observadores
  if (user && user.role !== 'observer') {
    items.splice(2, 0, {
      title: "Importar Dados",
      url: createPageUrl("Import"),
      icon: Upload,
    });
  }

  // Adicionar menu de usuários apenas para admins
  if (user && AuthService.canManageUsers()) {
    items.push({
      title: "Gerenciar Usuários",
      url: createPageUrl("Users"),
      icon: Users,
    });
  }

  // Adicionar menu de auditoria apenas para admins e super admins
  if (user && AuthService.canManageUsers()) {
    items.push({
      title: "Auditoria",
      url: createPageUrl("Audit"),
      icon: Shield,
    });
  }

  return items;
};

// Componente interno que usa o hook useSidebar
function LayoutContent({ children, currentUser, navigationItems }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const { isOpen, isMobile, close } = useSidebar();

  // Detectar status de conexão
  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      // ✅ SOLUÇÃO: replace: true limpa histórico de páginas autenticadas
      // Permite que botão "Voltar" no login funcione corretamente
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Erro no logout:', error);
      // Forçar logout mesmo com erro
      localStorage.clear();
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex w-full bg-slate-50">
      <Sidebar>
        <SidebarHeader className="border-b border-slate-200 p-4">
          <div className="flex items-center gap-3">
            {isOpen && (
              <>
                <img 
                  src="/brazao_logo.png" 
                  alt="Prefeitura de Corumbá" 
                  className="w-10 h-10 object-contain flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <h2 className="font-bold text-[#003098] text-xs leading-tight truncate">Painel de Controle</h2>
                  <p className="text-[10px] text-[#0074f6] truncate">Parcerias com OSC</p>
                </div>
              </>
            )}
            {!isOpen && (
              <img 
                src="/brazao_logo.png" 
                alt="Prefeitura de Corumbá" 
                className="w-10 h-10 object-contain mx-auto"
              />
            )}
          </div>
        </SidebarHeader>
          
        <SidebarContent className="p-3 flex-1">
          <SidebarGroup>
            {isOpen && (
              <SidebarGroupLabel className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider px-3 py-2">
                Menu Principal
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {navigationItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton 
                      asChild 
                      className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                        location.pathname === item.url ? 'bg-blue-50 text-blue-700 shadow-sm' : ''
                      } ${!isOpen ? 'justify-center' : ''}`}
                      title={!isOpen ? item.title : undefined}
                    >
                      <Link 
                        to={item.url} 
                        className={`flex items-center gap-3 px-3 py-2.5 ${!isOpen ? 'justify-center' : ''}`}
                        onClick={() => {
                          // ✅ Fechar sidebar automaticamente no mobile após clicar
                          if (isMobile) {
                            close();
                          }
                        }}
                      >
                        <item.icon className="w-4 h-4 flex-shrink-0" />
                        {isOpen && <span className="font-medium text-sm truncate">{item.title}</span>}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        {/* Versão do Sistema - Rodapé Fixo */}
        <div className="border-t border-slate-200 p-3 bg-slate-50">
          {isOpen ? (
            <div className="text-center space-y-1">
              <p className="text-[10px] font-medium text-slate-500">Sistema de Parcerias v5.0</p>
              <p className="text-[9px] text-slate-400">Prefeitura de Corumbá/MS</p>
              <p className="text-[9px] text-slate-400">© 2024 - Todos os direitos reservados</p>
              {/* ✅ Dica do atalho de teclado */}
              <div className="pt-2 mt-2 border-t border-slate-200">
                <p className="text-[9px] text-slate-400 flex items-center justify-center gap-1">
                  <kbd className="px-1.5 py-0.5 text-[8px] font-semibold text-slate-600 bg-white border border-slate-300 rounded">Ctrl</kbd>
                  <span>+</span>
                  <kbd className="px-1.5 py-0.5 text-[8px] font-semibold text-slate-600 bg-white border border-slate-300 rounded">B</kbd>
                  <span className="ml-1">para alternar menu</span>
                </p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-1.5 h-1.5 bg-slate-300 rounded-full" />
            </div>
          )}
        </div>
      </Sidebar>

      <main className="flex-1 flex flex-col min-w-0">
        {/* Header com botão toggle e usuário */}
        <header className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-30">
          <div className="flex items-center justify-between gap-4">
            {/* Lado esquerdo: Toggle + Título */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <SidebarTrigger />
              <div className="min-w-0">
                <h1 className="text-base md:text-lg font-semibold text-[#003098] truncate">Controle de Parcerias</h1>
                <p className="text-xs text-slate-500 hidden sm:block">Sistema de Gestão de OSCs</p>
              </div>
            </div>

            {/* Lado direito: Usuário logado */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Info do usuário - Desktop */}
              <div className="hidden md:flex items-center gap-2">
                {/* Indicador de conexão */}
                <div className="relative">
                  <div 
                    className={`w-2 h-2 rounded-full ${
                      isOnline ? 'bg-green-500' : 'bg-red-500'
                    }`}
                    title={isOnline ? 'Sistema Online' : 'Sistema Offline'}
                  />
                  {isOnline && (
                    <div className="absolute inset-0 w-2 h-2 bg-green-500 rounded-full animate-ping opacity-75" />
                  )}
                </div>
                
                <div className="flex flex-col items-start">
                  <p className="text-sm font-medium text-slate-900">{currentUser?.name}</p>
                  <p className="text-xs text-slate-500">{currentUser?.role === 'super_admin' ? 'Super Admin' : currentUser?.role === 'admin' ? 'Administrador' : 'Usuário'}</p>
                </div>
              </div>

              {/* Avatar com menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 transition-colors"
                  title={currentUser?.name}
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-[#003098] to-[#0074f6] rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400 hidden md:block" />
                </button>

                {/* Dropdown menu */}
                {showUserMenu && (
                  <>
                    {/* Overlay para fechar ao clicar fora */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowUserMenu(false)}
                    />
                    
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-xl py-2 z-50">
                      {/* Info do usuário - Mobile */}
                      <div className="md:hidden px-4 py-3 border-b border-slate-200">
                        <div className="flex items-center gap-2 mb-2">
                          <div 
                            className={`w-2 h-2 rounded-full ${
                              isOnline ? 'bg-green-500' : 'bg-red-500'
                            }`}
                          />
                          <span className="text-[10px] text-slate-500">
                            {isOnline ? 'Online' : 'Offline'}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-slate-900">{currentUser?.name}</p>
                        <p className="text-xs text-slate-500">{currentUser?.email}</p>
                        <p className="text-xs text-[#0074f6] mt-1">{currentUser?.role === 'super_admin' ? 'Super Administrador' : currentUser?.role === 'admin' ? 'Administrador' : 'Usuário'}</p>
                      </div>

                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          setShowProfileModal(true);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        Meu Perfil
                      </button>

                      <hr className="my-2" />
                      
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Sair do Sistema
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </main>

      {/* Modal de Perfil */}
      <ProfileModal 
        isOpen={showProfileModal} 
        onClose={() => setShowProfileModal(false)} 
      />
    </div>
  );
}

// Componente principal que envolve com SidebarProvider
export default function Layout({ children, currentPageName }) {
  const currentUser = AuthService.getCurrentUser();
  const navigationItems = getNavigationItems(currentUser);

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      // ✅ SOLUÇÃO: replace() limpa histórico (melhor que href)
      // Permite que botão "Voltar" no login funcione corretamente
      window.location.replace('/login');
    } catch (error) {
      console.error('Erro no logout:', error);
      localStorage.clear();
      window.location.replace('/login');
    }
  };

  return (
    <SidebarProvider defaultOpen={true}>
      <LayoutContent 
        children={children} 
        currentUser={currentUser} 
        navigationItems={navigationItems}
      />
    </SidebarProvider>
  );
}